/* eslint-disable import/no-duplicates */
import { Suspense, useCallback, useEffect } from 'react';

import Stack from '@mui/material/Stack';

import dayjs from 'dayjs';
import { NuqsAdapter } from 'nuqs/adapters/react-router/v6';
import { usePostHog } from 'posthog-js/react';
import { useTranslation } from 'react-i18next';
import { RouterProvider } from 'react-router-dom';
import { Toaster } from 'sonner';

import 'translations';
import 'utils/yup';

import { Loader } from 'components/Loader';
import { useSession } from 'modules/accounts/hooks';
import { router } from 'routes';
import { useAppSelector } from 'store';
import { toLanguageDTO } from 'store/accounts/helpers';
import { Language } from 'store/accounts/models';
import { useChangeLanguageMutation } from 'store/api';
import type { Nullable } from 'types';
import { PersistService } from 'utils/persist';

import 'globals.css';
import 'swiper/css';
import 'swiper/css/virtual';

export function App() {
  const posthog = usePostHog();

  const { isAuthenticated } = useAppSelector((state) => state.auth);
  const { i18n } = useTranslation();

  const { session } = useSession({ skip: !isAuthenticated });

  const [changeLanguage] = useChangeLanguageMutation();

  const updateLanguage = useCallback(() => {
    if (!session || !isAuthenticated) return;

    // No need to update language when it's already set
    if (session.language) return;

    try {
      changeLanguage({ language: toLanguageDTO(i18n.language as Language) });
    } catch {
      // noop
    }
  }, [changeLanguage, i18n.language, isAuthenticated, session]);

  useEffect(() => {
    if (!session?.userId) return;

    posthog?.identify(session.userId.toString());
  }, [posthog, session?.userId]);

  useEffect(() => {
    if (!session) return;

    let rawPersistedLanguage: Nullable<string> = null;

    try {
      rawPersistedLanguage = localStorage.getItem('language');
    } catch {
      // localStorage unavailable (e.g. restricted Safari/WebKit contexts) - ignore
    }

    const persistedLanguage = PersistService.get('language') ?? rawPersistedLanguage ?? Language.EN;

    i18n.changeLanguage(session.language ?? i18n.language ?? persistedLanguage);
    dayjs.locale(session.language ?? i18n.language ?? persistedLanguage);
  }, [i18n, session]);

  useEffect(() => {
    updateLanguage();
  }, [updateLanguage]);

  return (
    <NuqsAdapter>
      <Suspense
        fallback={
          <Stack minHeight="100vh" alignItems="center" justifyContent="center">
            <Loader />
          </Stack>
        }
      >
        <RouterProvider router={router} />
      </Suspense>

      <Toaster
        position="bottom-center"
        richColors
        toastOptions={{
          classNames: {
            content: 'Sonner__toast--content',
            error: 'Sonner__toast--error',
            info: 'Sonner__toast--info',
            success: 'Sonner__toast--success',
            warning: 'Sonner__toast--warning',
            title: 'Sonner__toast--title',
            toast: 'Sonner__toast',
            description: 'Sonner__toast--description',
          },
        }}
      />
    </NuqsAdapter>
  );
}
